/* eslint-disable react/prop-types */
/* eslint-disable react/forbid-prop-types */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import {
  Field,
  change,
  reduxForm,
  submit,
  reset,
  formValueSelector,
} from 'redux-form';
import { Badge, Button, Col, Form, FormGroup, Label, Row } from 'reactstrap';

import Loader from '../../../common/Loader';
import Alert from '../../../common/informative/Alert';
import FormFooter from '../../../common/forms/FormFooter';
import FormItem from '../../../common/forms/FormItem';
import TextInput from '../../../common/forms/input/TextInput';
import Select from '../../../common/forms/select/Select';
import MechanicSelect from '../../../common/forms/select/MechanicSelect';
import Modal from '../../../common/modal/Modal';
import { Table } from '../../../common/Table';

import { DATE_TIME_FORMAT } from '../../../../config/locale';
import { enumToSelectOptions } from '../../../../utils/enum';
import { isRequired } from '../../../../utils/validators';

import {
  getMaintenanceFailModeActivities,
  clearMaintenanceFailModeActivities,
} from '../../../../actions';

import { MAINTENANCE_WARNING_TYPE } from '../../../../config/constants';

import './MaintenanceChecklistAdditionalExecutionForm.css';

export const MAINTENANCE_CHECKLIST_ADDITIONAL_EXECUTION_FORM_NAME =
  'MaintenanceChecklistAdditionalExecutionForm';

export const MAINTENANCE_CHECKLIST_NESTED_ACTIVITY_FORM_NAME =
  'MaintenanceChecklistNestedActivityForm';

const ACTIVITY_STATUS_OPTIONS = [
  {
    value: 'PENDING',
    label: 'Pendiente',
  },
  {
    value: 'IN_PROGRESS',
    label: 'En progreso',
  },
  {
    value: 'DONE',
    label: 'Realizado',
  },
];

const MAX_EXECUTION_HOURS_WITHOUT_WARNING = 72;

const parseDateValue = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const normalizedValue =
    typeof value === 'string' ? value.replace(' ', 'T') : value;

  const date = new Date(normalizedValue);

  return Number.isNaN(date.getTime()) ? null : date;
};

const getExecutionHours = (startDate, endDate) => {
  const parsedStartDate = parseDateValue(startDate);
  const parsedEndDate = parseDateValue(endDate);

  if (!parsedStartDate || !parsedEndDate) {
    return 0;
  }

  return (
    (parsedEndDate.getTime() - parsedStartDate.getTime()) / (1000 * 60 * 60)
  );
};

const formatDateTime = (value) => {
  const date = parseDateValue(value);

  if (!date) return '-';

  return date.toLocaleString('es-PE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatExecutionDuration = (executionHours) => {
  const totalMinutes = Math.floor(executionHours * 60);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const durationParts = [];

  if (days > 0) {
    durationParts.push(`${days} ${days === 1 ? 'día' : 'días'}`);
  }

  if (hours > 0) {
    durationParts.push(`${hours} ${hours === 1 ? 'hora' : 'horas'}`);
  }

  if (minutes > 0) {
    durationParts.push(`${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`);
  }

  return durationParts.join(', ');
};

const toJSArray = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value;
  }

  if (Immutable.List.isList(value)) {
    return value.toJS();
  }

  return [];
};

const toPlainObject = (value) => {
  if (!value) return {};

  if (Immutable.Map.isMap(value)) {
    return value.toJS();
  }

  if (typeof value === 'object') {
    return value;
  }

  return {};
};

const isRequiredArray = (value) =>
  toJSArray(value).filter(Boolean).length ? undefined : 'Requerido';

const getInputValue = (value) =>
  value && value.target ? value.target.value : value;

const toDateTimeLocalValue = (value) => {
  if (!value) return '';

  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
      return value.slice(0, 16);
    }

    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(value)) {
      return value.replace(' ', 'T').slice(0, 16);
    }
  }

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);

  return localDate.toISOString().slice(0, 16);
};

const getDefaultDateRange = () => {
  const currentDate = new Date();
  const endDateValue = new Date(currentDate);

  endDateValue.setHours(endDateValue.getHours() + 1);

  return {
    startDate: toDateTimeLocalValue(currentDate),
    endDate: toDateTimeLocalValue(endDateValue),
  };
};

const getId = (value) => {
  if (!value) return null;

  if (typeof value === 'number' || typeof value === 'string') {
    return value;
  }

  if (Immutable.Map.isMap(value)) {
    return (
      value.get('value') ||
      value.get('id') ||
      value.get('employeeId') ||
      value.get('mechanicId') ||
      null
    );
  }

  return (
    value.value || value.id || value.employeeId || value.mechanicId || null
  );
};

const isInProgressStatus = (status) => getId(status) === 'IN_PROGRESS';

const isPendingStatus = (status) => getId(status) === 'PENDING';

const isDoneStatus = (status) => getId(status) === 'DONE';

const getOptionLabel = (value) => {
  if (!value) return '-';

  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }

  if (Immutable.Map.isMap(value)) {
    return (
      value.get('label') ||
      value.get('name') ||
      value.get('description') ||
      value.get('activityDescription') ||
      value.get('fullName') ||
      '-'
    );
  }

  return (
    value.label ||
    value.name ||
    value.description ||
    value.activityDescription ||
    value.fullName ||
    '-'
  );
};

const getEmployeeLabel = (employee) => {
  const directLabel = getOptionLabel(employee);

  if (directLabel !== '-') {
    return directLabel;
  }

  const employeeObject = toPlainObject(employee);

  const nestedValues = [
    employeeObject.employee,
    employeeObject.mechanic,
    employeeObject.person,
    employeeObject.user,
  ];

  const nestedValue = nestedValues.find(
    (value) => value && getOptionLabel(value) !== '-',
  );

  if (nestedValue) {
    return getOptionLabel(nestedValue);
  }

  const employeeId = getId(employee);

  return employeeId ? `${employeeId}` : '-';
};

const buildEmployeeOptionsFromValue = (value) =>
  toJSArray(value)
    .map((employee) => {
      const employeeId = getId(employee);

      if (!employeeId) return null;

      const employeeObject = toPlainObject(employee);
      const label = getEmployeeLabel(employee);

      return {
        ...employeeObject,
        value: employeeId,
        label,
        employeeId,
      };
    })
    .filter(Boolean);

const buildParentEmployeeOptions = (activity) => {
  if (!activity) return [];

  const employees = toJSArray(activity.employee).filter(Boolean);

  if (employees.length) {
    return buildEmployeeOptionsFromValue(employees);
  }

  const employeeIds = toJSArray(activity.employeeId).filter(Boolean);

  if (employeeIds.length) {
    return buildEmployeeOptionsFromValue(employeeIds);
  }

  return buildEmployeeOptionsFromValue(
    activity.maintenancePlanningActivityExecutionEmployeeList,
  );
};

const filterEmployeesByParent = (employees, parentEmployeeOptions) => {
  const allowedEmployeeIds = parentEmployeeOptions.map((employee) =>
    String(getId(employee)),
  );

  return toJSArray(employees).filter((employee) =>
    allowedEmployeeIds.includes(String(getId(employee))),
  );
};

const getActivityLabel = (checklistActivity) => {
  if (!checklistActivity) return '-';

  return (
    (checklistActivity.activity && checklistActivity.activity.name) ||
    (checklistActivity.activity && checklistActivity.activity.description) ||
    checklistActivity.activityName ||
    checklistActivity.description ||
    '-'
  );
};

const getFailModeLabel = (checklistActivity) => {
  if (!checklistActivity) return '-';

  return (
    (checklistActivity.maintenanceFailMode &&
      checklistActivity.maintenanceFailMode.description) ||
    checklistActivity.maintenanceFailModeDescription ||
    '-'
  );
};

const buildTempId = () => `${Date.now()}-${Math.random()}`;

const getStatusOption = (status) => {
  if (!status) {
    return ACTIVITY_STATUS_OPTIONS[0];
  }

  if (typeof status === 'object') {
    return status;
  }

  return (
    ACTIVITY_STATUS_OPTIONS.find((option) => option.value === status) ||
    ACTIVITY_STATUS_OPTIONS[0]
  );
};

const buildActivityOptions = (activities) =>
  toJSArray(activities).map((activity) => ({
    value: activity.id,
    label: activity.companyActivityId
      ? `${activity.companyActivityId} - ${activity.name}`
      : activity.name,
    raw: activity,
  }));

const hasEmployees = (activityExecution) => {
  if (Object.prototype.hasOwnProperty.call(activityExecution, 'employee')) {
    return toJSArray(activityExecution.employee).filter(Boolean).length > 0;
  }

  const employeeIds = toJSArray(activityExecution.employeeId).filter(Boolean);

  if (employeeIds.length > 0) {
    return true;
  }

  const executionEmployees = toJSArray(
    activityExecution.maintenancePlanningActivityExecutionEmployeeList,
  ).filter(Boolean);

  return executionEmployees.length > 0;
};

const normalizeAdditionalChecklistActivities = (maintenanceChecklist) => {
  const activityList = toJSArray(
    maintenanceChecklist &&
      maintenanceChecklist.maintenanceChecklistActivityList,
  );

  return activityList
    .filter((activity) => activity.basic === false || activity.basic === null)
    .map((activity) => {
      const defaultDateRange = getDefaultDateRange();

      const execution = toPlainObject(
        activity.maintenancePlanningActivityExecution,
      );

      const executionEmployees = toJSArray(execution.employeeList).filter(
        Boolean,
      );

      const employeeOptions = buildEmployeeOptionsFromValue(executionEmployees);

      const executionEmployeePayload = executionEmployees
        .map((employee) => ({
          employeeId: getId(employee),
        }))
        .filter((employee) => employee.employeeId);

      const hasStartDate =
        execution.startDate !== null && execution.startDate !== undefined;

      const hasEndDate =
        execution.endDate !== null && execution.endDate !== undefined;

      const hasComment =
        execution.comment !== null && execution.comment !== undefined;

      const hasStatus =
        execution.status !== null && execution.status !== undefined;

      const lockedExecution =
        execution.status === 'IN_PROGRESS' &&
        execution.advancePercentage !== null &&
        execution.advancePercentage !== undefined;

      return {
        ...activity,

        tempId: activity.tempId || activity.id || buildTempId(),

        status: getStatusOption(hasStatus ? execution.status : 'PENDING'),

        startDate: hasStartDate
          ? toDateTimeLocalValue(execution.startDate)
          : defaultDateRange.startDate,

        endDate: hasEndDate
          ? toDateTimeLocalValue(execution.endDate)
          : defaultDateRange.endDate,

        employee: employeeOptions,

        maintenancePlanningActivityExecutionEmployeeList:
          executionEmployeePayload,

        advancePercentage:
          execution.advancePercentage !== null &&
          execution.advancePercentage !== undefined
            ? execution.advancePercentage
            : null,

        executionComment: hasComment ? execution.comment : '',

        nestedActivityExecutionList: activity.nestedActivityExecutionList || [],

        lockedExecution,
      };
    });
};

const buildEmployeeList = (activityExecution) => {
  if (Object.prototype.hasOwnProperty.call(activityExecution, 'employee')) {
    return toJSArray(activityExecution.employee)
      .filter(Boolean)
      .map((employee) => ({
        employeeId: getId(employee),
      }))
      .filter((employee) => employee.employeeId);
  }

  const employeeIds = toJSArray(activityExecution.employeeId).filter(Boolean);

  if (employeeIds.length) {
    return employeeIds
      .map((employee) => ({
        employeeId: getId(employee),
      }))
      .filter((employee) => employee.employeeId);
  }

  return toJSArray(
    activityExecution.maintenancePlanningActivityExecutionEmployeeList,
  )
    .filter(Boolean)
    .map((employee) => ({
      employeeId: getId(employee),
    }))
    .filter((employee) => employee.employeeId);
};

const normalizeNestedOccurrence = (values, parentActivity) => {
  const parentEmployeeOptions = buildParentEmployeeOptions(parentActivity);

  return {
    ...values,

    tempId: values.tempId || buildTempId(),

    maintenanceFailModeId: parentActivity.maintenanceFailModeId,

    maintenanceFailModeCategoryId: parentActivity.maintenanceFailModeCategoryId,

    maintenanceWarningType: values.maintenanceWarningType,

    activityId: values.activityId || parentActivity.activityId,

    activity:
      values.activityId && values.activityId.raw ? values.activityId.raw : null,

    status: values.status || {
      value: 'PENDING',
      label: 'Pendiente',
    },

    startDate: toDateTimeLocalValue(values.startDate) || null,

    endDate: toDateTimeLocalValue(values.endDate) || null,

    advancePercentage: values.advancePercentage || null,

    employee: filterEmployeesByParent(values.employee, parentEmployeeOptions),

    executionComment: values.executionComment || values.comments || '',
  };
};

const buildExecutionWarning = ({
  execution,
  type,
  activityLabel,
  parentActivityLabel,
  key,
}) => {
  if (!isDoneStatus(execution.status)) {
    return null;
  }

  const executionHours = getExecutionHours(
    execution.startDate,
    execution.endDate,
  );

  if (executionHours <= MAX_EXECUTION_HOURS_WITHOUT_WARNING) {
    return null;
  }

  return {
    key,
    type,
    activityLabel,
    parentActivityLabel,
    startDate: execution.startDate,
    endDate: execution.endDate,
    executionHours,
  };
};

const getLongExecutionWarnings = (activities) =>
  toJSArray(activities).reduce((warnings, activity, activityIndex) => {
    const activityLabel = getActivityLabel(activity);

    const activityWarning = buildExecutionWarning({
      execution: activity,
      type: 'Actividad adicional',
      activityLabel,
      parentActivityLabel: null,
      key: `activity-${activity.tempId || activity.id || activityIndex}`,
    });

    if (activityWarning) {
      warnings.push(activityWarning);
    }

    toJSArray(activity.nestedActivityExecutionList).forEach(
      (nestedActivity, nestedIndex) => {
        const nestedLabelFromEntity = getActivityLabel(nestedActivity);

        const nestedActivityLabel =
          nestedLabelFromEntity !== '-'
            ? nestedLabelFromEntity
            : getOptionLabel(nestedActivity.activityId);

        const nestedWarning = buildExecutionWarning({
          execution: nestedActivity,
          type: 'Actividad anidada',
          activityLabel: nestedActivityLabel,
          parentActivityLabel: activityLabel,
          key: `nested-${activity.tempId || activity.id || activityIndex}-${
            nestedActivity.tempId || nestedActivity.id || nestedIndex
          }`,
        });

        if (nestedWarning) {
          warnings.push(nestedWarning);
        }
      },
    );

    return warnings;
  }, []);

const MaintenanceChecklistNestedActivityFormBase = ({
  handleSubmit,
  onSubmit,
  activityOptions,
  loadingActivities,
  selectedStatus,
  parentEmployeeOptions,
}) => {
  const executionFieldsRequired = !isPendingStatus(selectedStatus);

  if (loadingActivities) {
    return <Loader />;
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <FormGroup row>
        <FormItem label="Tipo de Ocurrencia" required>
          <Field
            name="maintenanceWarningType"
            component={Select}
            options={enumToSelectOptions(MAINTENANCE_WARNING_TYPE)}
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>

      <FormGroup row>
        <FormItem label="Estado" required>
          <Field
            name="status"
            component={Select}
            options={ACTIVITY_STATUS_OPTIONS}
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>

      {isInProgressStatus(selectedStatus) && (
        <FormGroup row>
          <FormItem label="% Avance" required>
            <Field
              name="advancePercentage"
              component={TextInput}
              type="number"
              min="0.01"
              max="99.9"
              step="0.01"
              validate={[isRequired]}
            />
          </FormItem>
        </FormGroup>
      )}

      <FormGroup row>
        <FormItem label="Actividad" required>
          <Field
            name="activityId"
            component={Select}
            options={activityOptions}
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>

      {activityOptions.length === 0 && (
        <Alert
          type="warning"
          message="No hay actividades configuradas para este modo de falla."
        />
      )}

      <FormGroup row>
        <FormItem label="Fecha/Hora Inicio" required={executionFieldsRequired}>
          <Field
            name="startDate"
            component={TextInput}
            type="datetime-local"
            placeholder={DATE_TIME_FORMAT}
            validate={executionFieldsRequired ? [isRequired] : []}
          />
        </FormItem>
      </FormGroup>

      <FormGroup row>
        <FormItem label="Fecha/Hora Final" required={executionFieldsRequired}>
          <Field
            name="endDate"
            component={TextInput}
            type="datetime-local"
            placeholder={DATE_TIME_FORMAT}
            validate={executionFieldsRequired ? [isRequired] : []}
          />
        </FormItem>
      </FormGroup>

      <FormGroup row>
        <FormItem label="Empleados" required={executionFieldsRequired}>
          <Field
            name="employee"
            component={Select}
            options={parentEmployeeOptions}
            isMulti
            validate={executionFieldsRequired ? [isRequiredArray] : []}
            disabled={parentEmployeeOptions.length === 0}
          />
        </FormItem>
      </FormGroup>

      {parentEmployeeOptions.length === 0 && (
        <Alert
          type="warning"
          message="Seleccione al menos un empleado en la actividad padre."
        />
      )}

      <FormGroup row>
        <FormItem label="Comentario de ejecución">
          <Field
            name="executionComment"
            component={TextInput}
            type="textarea"
            maxLength={200}
          />
        </FormItem>
      </FormGroup>
    </Form>
  );
};

MaintenanceChecklistNestedActivityFormBase.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  activityOptions: PropTypes.array,
  loadingActivities: PropTypes.bool,

  selectedStatus: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),

  parentEmployeeOptions: PropTypes.array,
};

MaintenanceChecklistNestedActivityFormBase.defaultProps = {
  activityOptions: [],
  loadingActivities: false,
  selectedStatus: null,
  parentEmployeeOptions: [],
};

const nestedFormSelector = formValueSelector(
  MAINTENANCE_CHECKLIST_NESTED_ACTIVITY_FORM_NAME,
);

const MaintenanceChecklistNestedActivityReduxForm = reduxForm({
  form: MAINTENANCE_CHECKLIST_NESTED_ACTIVITY_FORM_NAME,

  initialValues: {
    maintenanceWarningType: {
      value: 'WORK_REQUEST',
      label: 'Solicitud de Trabajo',
    },

    status: {
      value: 'PENDING',
      label: 'Pendiente',
    },

    startDate: getDefaultDateRange().startDate,

    endDate: getDefaultDateRange().endDate,

    employee: [],
  },
})(MaintenanceChecklistNestedActivityFormBase);

const MaintenanceChecklistNestedActivityForm = connect(
  (state) => ({
    selectedStatus: nestedFormSelector(state, 'status'),
  }),
  {
    dispatchChange: change,
  },
)(MaintenanceChecklistNestedActivityReduxForm);

export const MaintenanceChecklistAdditionalExecutionForm = ({
  handleSubmit,
  onSubmit,
  maintenanceChecklist,
  dispatchSubmit,
  dispatchChange,
  dispatchReset,
  loading,
  maintenanceFailModeActivities,
  loadingMaintenanceFailModeActivities,
  dispatchGetMaintenanceFailModeActivities,
  dispatchClearMaintenanceFailModeActivities,
}) => {
  const [additionalActivities, setAdditionalActivities] = useState(() =>
    normalizeAdditionalChecklistActivities(maintenanceChecklist),
  );

  const [selectedParentActivity, setSelectedParentActivity] = useState(null);
  const [selectedNestedDetail, setSelectedNestedDetail] = useState(null);
  const [showNestedModal, setShowNestedModal] = useState(false);

  const [formErrors, setFormErrors] = useState({});

  const [showExecutionWarning, setShowExecutionWarning] = useState(false);
  const [executionWarnings, setExecutionWarnings] = useState([]);
  const [pendingSubmitPayload, setPendingSubmitPayload] = useState(null);

  const nestedActivityOptions = useMemo(
    () => buildActivityOptions(maintenanceFailModeActivities),
    [maintenanceFailModeActivities],
  );

  const selectedParentEmployeeOptions = useMemo(
    () => buildParentEmployeeOptions(selectedParentActivity),
    [selectedParentActivity],
  );

  const getActivityError = (activity, field) =>
    formErrors[activity.tempId] && formErrors[activity.tempId][field];

  const clearActivityError = (tempId, field) => {
    setFormErrors((current) => {
      if (!current[tempId] || !current[tempId][field]) {
        return current;
      }

      const activityErrors = {
        ...current[tempId],
      };

      delete activityErrors[field];

      const nextErrors = {
        ...current,
      };

      if (Object.keys(activityErrors).length) {
        nextErrors[tempId] = activityErrors;
      } else {
        delete nextErrors[tempId];
      }

      return nextErrors;
    });
  };

  const clearActivityErrors = (tempId, fields) => {
    setFormErrors((current) => {
      if (!current[tempId]) {
        return current;
      }

      const activityErrors = {
        ...current[tempId],
      };

      fields.forEach((field) => {
        delete activityErrors[field];
      });

      const nextErrors = {
        ...current,
      };

      if (Object.keys(activityErrors).length) {
        nextErrors[tempId] = activityErrors;
      } else {
        delete nextErrors[tempId];
      }

      return nextErrors;
    });
  };

  const updateActivityValue = (tempId, field, value) => {
    setAdditionalActivities((current) =>
      current.map((activity) => {
        if (activity.tempId !== tempId) {
          return activity;
        }

        if (activity.lockedExecution) {
          return activity;
        }

        const nextActivity = {
          ...activity,
          [field]: value,
        };

        if (field === 'employee') {
          const parentEmployeeOptions =
            buildParentEmployeeOptions(nextActivity);

          return {
            ...nextActivity,

            nestedActivityExecutionList: toJSArray(
              nextActivity.nestedActivityExecutionList,
            ).map((nestedActivity) => ({
              ...nestedActivity,

              employee: filterEmployeesByParent(
                nestedActivity.employee,
                parentEmployeeOptions,
              ),

              employeeId: filterEmployeesByParent(
                nestedActivity.employeeId,
                parentEmployeeOptions,
              ),

              maintenancePlanningActivityExecutionEmployeeList:
                filterEmployeesByParent(
                  nestedActivity.maintenancePlanningActivityExecutionEmployeeList,
                  parentEmployeeOptions,
                ),
            })),
          };
        }

        return nextActivity;
      }),
    );

    clearActivityError(tempId, field);
  };

  const validateAdditionalActivities = () => {
    const errors = {};

    additionalActivities.forEach((activity) => {
      if (activity.lockedExecution || isPendingStatus(activity.status)) {
        return;
      }

      const activityErrors = {};

      if (!activity.startDate) {
        activityErrors.startDate = 'Requerido';
      }

      if (!activity.endDate) {
        activityErrors.endDate = 'Requerido';
      }

      if (!hasEmployees(activity)) {
        activityErrors.employee = 'Requerido';
      }

      if (
        isInProgressStatus(activity.status) &&
        (activity.advancePercentage === null ||
          activity.advancePercentage === undefined ||
          activity.advancePercentage === '')
      ) {
        activityErrors.advancePercentage = 'Requerido';
      }

      if (Object.keys(activityErrors).length) {
        errors[activity.tempId] = activityErrors;
      }
    });

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const onChangeAdditionalStatus = (activity, status) => {
    if (activity.lockedExecution) {
      return;
    }

    updateActivityValue(activity.tempId, 'status', status);

    if (isPendingStatus(status)) {
      clearActivityErrors(activity.tempId, [
        'startDate',
        'endDate',
        'employee',
      ]);

      updateActivityValue(activity.tempId, 'advancePercentage', null);

      return;
    }

    if (!isInProgressStatus(status)) {
      updateActivityValue(activity.tempId, 'advancePercentage', null);
    }
  };
  const onChangeAdditionalEmployees = (activity, event, value) => {
    if (activity.lockedExecution) {
      return;
    }

    const selectedEmployees =
      // eslint-disable-next-line no-nested-ternary
      value !== undefined
        ? value
        : event && event.target
        ? event.target.value
        : event;

    const employees = selectedEmployees || [];

    updateActivityValue(activity.tempId, 'employee', employees);

    if (toJSArray(employees).filter(Boolean).length > 0) {
      onChangeAdditionalStatus(activity, {
        value: 'DONE',
        label: 'Realizado',
      });
    }
  };

  const openNestedModal = (activity) => {
    if (activity.lockedExecution) {
      return;
    }

    const parentEmployeeOptions = buildParentEmployeeOptions(activity);

    if (!parentEmployeeOptions.length) {
      setFormErrors((current) => ({
        ...current,

        [activity.tempId]: {
          ...(current[activity.tempId] || {}),

          employee:
            'Seleccione al menos un empleado antes de agregar una anidada.',
        },
      }));

      return;
    }

    const defaultDateRange = getDefaultDateRange();

    setSelectedParentActivity(activity);

    dispatchReset(MAINTENANCE_CHECKLIST_NESTED_ACTIVITY_FORM_NAME);

    dispatchClearMaintenanceFailModeActivities();

    dispatchChange(
      MAINTENANCE_CHECKLIST_NESTED_ACTIVITY_FORM_NAME,
      'startDate',
      defaultDateRange.startDate,
    );

    dispatchChange(
      MAINTENANCE_CHECKLIST_NESTED_ACTIVITY_FORM_NAME,
      'endDate',
      defaultDateRange.endDate,
    );

    dispatchChange(
      MAINTENANCE_CHECKLIST_NESTED_ACTIVITY_FORM_NAME,
      'employee',
      [],
    );

    if (activity.maintenanceFailModeId) {
      dispatchGetMaintenanceFailModeActivities({
        maintenanceFailModeId: activity.maintenanceFailModeId,
      });
    }

    setShowNestedModal(true);
  };

  const closeNestedModal = () => {
    setShowNestedModal(false);
    setSelectedParentActivity(null);

    dispatchReset(MAINTENANCE_CHECKLIST_NESTED_ACTIVITY_FORM_NAME);

    dispatchClearMaintenanceFailModeActivities();
  };

  const markInProgress = (activity) => {
    if (activity.lockedExecution) {
      return;
    }

    onChangeAdditionalStatus(activity, {
      value: 'IN_PROGRESS',
      label: 'En progreso',
    });
  };

  const markDone = (activity) => {
    if (activity.lockedExecution) {
      return;
    }

    onChangeAdditionalStatus(activity, {
      value: 'DONE',
      label: 'Realizado',
    });
  };

  const onSubmitNestedActivity = (values) => {
    if (!selectedParentActivity || selectedParentActivity.lockedExecution) {
      return;
    }

    const nestedActivity = normalizeNestedOccurrence(
      values,
      selectedParentActivity,
    );

    setAdditionalActivities((current) =>
      current.map((activity) => {
        if (activity.tempId !== selectedParentActivity.tempId) {
          return activity;
        }

        return {
          ...activity,

          nestedActivityExecutionList: [
            ...(activity.nestedActivityExecutionList || []),

            nestedActivity,
          ],
        };
      }),
    );

    closeNestedModal();
  };

  const removeNestedActivity = (parentTempId, nestedTempId) => {
    setAdditionalActivities((current) =>
      current.map((activity) => {
        if (activity.tempId !== parentTempId || activity.lockedExecution) {
          return activity;
        }

        return {
          ...activity,

          nestedActivityExecutionList: toJSArray(
            activity.nestedActivityExecutionList,
          ).filter((nested) => nested.tempId !== nestedTempId),
        };
      }),
    );
  };

  const buildNestedPayload = (nestedActivity) => ({
    maintenanceFailModeId: getId(nestedActivity.maintenanceFailModeId),

    maintenanceFailModeCategoryId: getId(
      nestedActivity.maintenanceFailModeCategoryId,
    ),

    maintenanceWarningType: getId(nestedActivity.maintenanceWarningType),

    activityId: getId(nestedActivity.activityId),

    status: getId(nestedActivity.status) || 'PENDING',

    startDate: toDateTimeLocalValue(nestedActivity.startDate) || null,

    endDate: toDateTimeLocalValue(nestedActivity.endDate) || null,

    advancePercentage:
      nestedActivity.advancePercentage !== null &&
      nestedActivity.advancePercentage !== undefined
        ? nestedActivity.advancePercentage
        : null,

    comment:
      nestedActivity.executionComment ||
      nestedActivity.comment ||
      nestedActivity.comments ||
      '',

    recurrence: getId(nestedActivity.recurrence),

    maintenancePlanningActivityExecutionEmployeeList:
      buildEmployeeList(nestedActivity),

    nestedActivityExecutionList: toJSArray(
      nestedActivity.nestedActivityExecutionList,
    ).map(buildNestedPayload),
  });

  const buildActivityPayload = (activity) => ({
    maintenanceChecklistActivityId: activity.id,

    maintenancePlanningActivityExecutionId:
      activity.maintenancePlanningActivityExecutionId,

    maintenanceFailModeId: activity.maintenanceFailModeId,

    maintenanceFailModeCategoryId: activity.maintenanceFailModeCategoryId,

    activityId: activity.activityId,

    status: getId(activity.status) || 'PENDING',

    startDate: toDateTimeLocalValue(activity.startDate) || null,

    endDate: toDateTimeLocalValue(activity.endDate) || null,

    advancePercentage:
      activity.advancePercentage !== null &&
      activity.advancePercentage !== undefined
        ? activity.advancePercentage
        : null,

    comment:
      activity.executionComment !== null &&
      activity.executionComment !== undefined
        ? activity.executionComment
        : '',

    maintenancePlanningActivityExecutionEmployeeList:
      buildEmployeeList(activity),

    nestedActivityExecutionList: toJSArray(
      activity.nestedActivityExecutionList,
    ).map(buildNestedPayload),
  });

  const buildSubmitPayload = () => ({
    maintenanceChecklistId: maintenanceChecklist.id,

    serviceOrderId: maintenanceChecklist.serviceOrderId,

    registeredBusId: maintenanceChecklist.registeredBusId,

    maintenanceChecklistAdditionalActivityExecutionList: additionalActivities
      .filter((activity) => !activity.lockedExecution)
      .map(buildActivityPayload),
  });

  const closeExecutionWarning = () => {
    setShowExecutionWarning(false);
    setExecutionWarnings([]);
    setPendingSubmitPayload(null);
  };

  const continueExecutionSubmit = async () => {
    const payload = pendingSubmitPayload;

    closeExecutionWarning();

    if (payload) {
      await onSubmit(payload);
    }
  };

  const onHandleSubmit = () => {
    if (!validateAdditionalActivities()) {
      return;
    }

    const payload = buildSubmitPayload();

    const warnings = getLongExecutionWarnings(additionalActivities);

    if (warnings.length > 0) {
      setPendingSubmitPayload(payload);
      setExecutionWarnings(warnings);
      setShowExecutionWarning(true);
      return;
    }

    onSubmit(payload);
  };

  const renderNestedDetail = () => {
    if (!selectedNestedDetail) {
      return null;
    }

    return (
      <div>
        <p>
          <strong>Tipo de ocurrencia:</strong>{' '}
          {getOptionLabel(selectedNestedDetail.maintenanceWarningType)}
        </p>

        <p>
          <strong>Actividad:</strong>{' '}
          {getOptionLabel(selectedNestedDetail.activityId)}
        </p>

        <p>
          <strong>Estado:</strong> {getOptionLabel(selectedNestedDetail.status)}
        </p>

        {isInProgressStatus(selectedNestedDetail.status) && (
          <p>
            <strong>% Avance:</strong>{' '}
            {selectedNestedDetail.advancePercentage || '-'}
          </p>
        )}

        <p>
          <strong>Inicio:</strong> {selectedNestedDetail.startDate || '-'}
        </p>

        <p>
          <strong>Fin:</strong> {selectedNestedDetail.endDate || '-'}
        </p>

        <p>
          <strong>Empleados:</strong>{' '}
          {toJSArray(selectedNestedDetail.employee)
            .map((employee) => getEmployeeLabel(employee))
            .join(', ') || '-'}
        </p>

        <p>
          <strong>Comentario de ejecución:</strong>{' '}
          {selectedNestedDetail.executionComment ||
            selectedNestedDetail.comments ||
            '-'}
        </p>
      </div>
    );
  };

  const getNestedColumns = (parentActivity) => [
    {
      Header: 'Tipo',
      accessor: 'maintenanceWarningTypeLabel',
      className: 'text-center',
    },
    {
      Header: 'Actividad',
      accessor: 'activityLabel',
      className: 'text-center',
    },
    {
      Header: 'Estado',
      accessor: 'statusLabel',
      className: 'text-center',
    },
    {
      Header: '% Avance',
      accessor: 'advancePercentageLabel',
      className: 'text-center',
    },
    {
      Header: 'Inicio',
      accessor: 'startDateLabel',
      className: 'text-center',
    },
    {
      Header: 'Fin',
      accessor: 'endDateLabel',
      className: 'text-center',
    },
    {
      Header: 'Comentario de ejecución',
      accessor: 'commentLabel',
      className: 'text-center',
    },
    {
      Header: 'Acciones',
      id: 'actions',
      className: 'text-center',
      filterable: false,
      sortable: false,
      notNavigateToModel: true,

      Cell: ({ original }) => (
        <>
          <Button
            type="button"
            color="info"
            outline
            size="sm"
            className="mr-2"
            onClick={() => setSelectedNestedDetail(original)}
          >
            Ver
          </Button>

          <Button
            type="button"
            color="danger"
            outline
            size="sm"
            onClick={() =>
              removeNestedActivity(parentActivity.tempId, original.tempId)
            }
            disabled={parentActivity.lockedExecution}
          >
            <i className="fa fa-trash" />
          </Button>
        </>
      ),
    },
  ];

  const renderNestedTable = (activity) => {
    const nestedList = toJSArray(activity.nestedActivityExecutionList);

    if (!nestedList.length) {
      return (
        <Alert
          type="info"
          message="No se han agregado tareas adicionales para esta ocurrencia."
        />
      );
    }

    const tableData = nestedList.map((nested) => ({
      ...nested,

      maintenanceWarningTypeLabel: getOptionLabel(
        nested.maintenanceWarningType,
      ),

      activityLabel: getOptionLabel(nested.activityId),

      statusLabel: getOptionLabel(nested.status),

      advancePercentageLabel: isInProgressStatus(nested.status)
        ? nested.advancePercentage || '-'
        : '-',

      startDateLabel: nested.startDate || '-',

      endDateLabel: nested.endDate || '-',

      commentLabel:
        nested.executionComment || nested.comment || nested.comments || '-',
    }));

    return (
      <Table
        data={tableData}
        columns={getNestedColumns(activity)}
        manual={false}
        filterable={false}
        showPagination={nestedList.length > 5}
        defaultPageSize={5}
        keyField="tempId"
      />
    );
  };

  if (loading) {
    return <Loader />;
  }

  if (!additionalActivities.length) {
    return (
      <Alert
        type="info"
        message="No hay actividades adicionales para ejecutar."
      />
    );
  }

  return (
    <Form onSubmit={handleSubmit(onHandleSubmit)}>
      {additionalActivities.map((activity) => (
        <div key={activity.tempId} className="border rounded p-3 mb-3 bg-white">
          <Row className="align-items-start">
            <Col xs="12" lg="8">
              <h4 className="mb-1">
                {getActivityLabel(activity)}{' '}
                <Badge color="secondary" pill>
                  Adicional
                </Badge>
              </h4>

              <p className="text-muted mb-1">
                <strong>Modo de falla:</strong> {getFailModeLabel(activity)}
              </p>
            </Col>

            <Col xs="12" lg="4" className="text-lg-right">
              <Button
                type="button"
                color="warning"
                outline
                className="mr-2 mb-2"
                onClick={() => markInProgress(activity)}
                disabled={activity.lockedExecution}
              >
                En progreso
              </Button>

              <Button
                type="button"
                color="success"
                outline
                className="mb-2"
                onClick={() => markDone(activity)}
                disabled={activity.lockedExecution}
              >
                Realizado
              </Button>
            </Col>
          </Row>

          <Row>
            <Col xs="12" md="6" lg="3">
              <FormGroup>
                <Label>Estado</Label>

                <Select
                  input={{
                    value: activity.status,

                    onChange: (value) =>
                      onChangeAdditionalStatus(activity, value),
                  }}
                  meta={{}}
                  options={ACTIVITY_STATUS_OPTIONS}
                  disabled={activity.lockedExecution}
                />
              </FormGroup>
            </Col>

            {isInProgressStatus(activity.status) && (
              <Col xs="12" md="6" lg="3">
                <FormGroup>
                  <Label>
                    % Avance <span className="text-danger">*</span>
                  </Label>

                  <TextInput
                    input={{
                      value: activity.advancePercentage || '',
                      onChange: (value) =>
                        updateActivityValue(
                          activity.tempId,
                          'advancePercentage',
                          getInputValue(value),
                        ),
                    }}
                    meta={{
                      touched: !!getActivityError(
                        activity,
                        'advancePercentage',
                      ),
                      error: getActivityError(activity, 'advancePercentage'),
                    }}
                    type="number"
                    min="0.01"
                    max="99.9"
                    step="0.01"
                    className="no-spin"
                    disabled={activity.lockedExecution}
                  />
                </FormGroup>
              </Col>
            )}

            <Col xs="12" md="6" lg="3">
              <FormGroup>
                <Label>
                  Inicio{' '}
                  {!isPendingStatus(activity.status) && (
                    <span className="text-danger">*</span>
                  )}
                </Label>

                <TextInput
                  input={{
                    value: activity.startDate || '',

                    onChange: (value) =>
                      updateActivityValue(
                        activity.tempId,
                        'startDate',
                        getInputValue(value),
                      ),
                  }}
                  meta={{
                    touched: !!getActivityError(activity, 'startDate'),

                    error: getActivityError(activity, 'startDate'),
                  }}
                  type="datetime-local"
                  placeholder={DATE_TIME_FORMAT}
                  disabled={activity.lockedExecution}
                />
              </FormGroup>
            </Col>

            <Col xs="12" md="6" lg="3">
              <FormGroup>
                <Label>
                  Fin{' '}
                  {!isPendingStatus(activity.status) && (
                    <span className="text-danger">*</span>
                  )}
                </Label>

                <TextInput
                  input={{
                    value: activity.endDate || '',

                    onChange: (value) =>
                      updateActivityValue(
                        activity.tempId,
                        'endDate',
                        getInputValue(value),
                      ),
                  }}
                  meta={{
                    touched: !!getActivityError(activity, 'endDate'),

                    error: getActivityError(activity, 'endDate'),
                  }}
                  type="datetime-local"
                  placeholder={DATE_TIME_FORMAT}
                  disabled={activity.lockedExecution}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col xs="12" md="6">
              <FormGroup>
                <Label>
                  Empleados{' '}
                  {!isPendingStatus(activity.status) && (
                    <span className="text-danger">*</span>
                  )}
                </Label>

                <MechanicSelect
                  input={{
                    value: activity.employee || [],

                    onChange: (event, value) =>
                      onChangeAdditionalEmployees(activity, event, value),
                  }}
                  meta={{
                    touched: !!getActivityError(activity, 'employee'),
                    error: getActivityError(activity, 'employee'),
                  }}
                  isMulti
                  disabled={activity.lockedExecution}
                />
              </FormGroup>
            </Col>

            <Col xs="12" md="6">
              <FormGroup>
                <Label>Comentario de ejecución</Label>

                <TextInput
                  input={{
                    value: activity.executionComment || '',

                    onChange: (value) =>
                      updateActivityValue(
                        activity.tempId,
                        'executionComment',
                        getInputValue(value),
                      ),
                  }}
                  meta={{}}
                  type="textarea"
                  maxLength={200}
                  disabled={activity.lockedExecution}
                />
              </FormGroup>
            </Col>
          </Row>

          <div className="border rounded p-3 mt-3">
            <div className="d-flex align-items-center justify-content-between flex-wrap mb-3">
              <h5 className="mb-0">Tareas Adicionales</h5>

              <Button
                type="button"
                color="success"
                outline
                size="sm"
                onClick={() => openNestedModal(activity)}
                disabled={activity.lockedExecution}
              >
                <i className="fa fa-plus mr-1" />
                Agregar adicional
              </Button>
            </div>

            {renderNestedTable(activity)}
          </div>
        </div>
      ))}

      <Modal
        show={showNestedModal}
        title="Agregar tarea adicional"
        body={
          <MaintenanceChecklistNestedActivityForm
            onSubmit={onSubmitNestedActivity}
            activityOptions={nestedActivityOptions}
            loadingActivities={loadingMaintenanceFailModeActivities}
            parentEmployeeOptions={selectedParentEmployeeOptions}
          />
        }
        onClickClose={closeNestedModal}
        onClickCancel={closeNestedModal}
        onClickConfirm={() =>
          dispatchSubmit(MAINTENANCE_CHECKLIST_NESTED_ACTIVITY_FORM_NAME)
        }
        confirmButtonText="Agregar"
        cancelButtonText="Cancelar"
      />

      <Modal
        show={!!selectedNestedDetail}
        title="Detalle de tarea anidada"
        body={renderNestedDetail()}
        onClickClose={() => setSelectedNestedDetail(null)}
      />

      <Modal
        show={showExecutionWarning}
        title="Advertencia de cierre"
        size="lg"
        body={
          <div>
            <p>
              Está cerrando actividades con más de 3 días de ejecución. Revise
              el detalle antes de continuar.
            </p>

            <p>
              <strong>
                Ejecuciones encontradas: {executionWarnings.length}
              </strong>
            </p>

            {executionWarnings.map((warning, index) => (
              <div key={warning.key} className="border rounded p-3 mb-3">
                <p className="mb-2">
                  <strong>
                    {index + 1}. {warning.activityLabel}
                  </strong>
                </p>

                <p className="mb-1">
                  <strong>Tipo:</strong> {warning.type}
                </p>

                {warning.parentActivityLabel && (
                  <p className="mb-1">
                    <strong>Actividad padre:</strong>{' '}
                    {warning.parentActivityLabel}
                  </p>
                )}

                <p className="mb-1">
                  <strong>Fecha/Hora Inicio:</strong>{' '}
                  {formatDateTime(warning.startDate)}
                </p>

                <p className="mb-1">
                  <strong>Fecha/Hora Final:</strong>{' '}
                  {formatDateTime(warning.endDate)}
                </p>

                <p className="mb-0">
                  <strong>Tiempo de ejecución:</strong>{' '}
                  {formatExecutionDuration(warning.executionHours)}
                </p>
              </div>
            ))}

            <p className="mb-0">¿Desea cerrar estas actividades y continuar?</p>
          </div>
        }
        onClickClose={closeExecutionWarning}
        onClickCancel={closeExecutionWarning}
        onClickConfirm={continueExecutionSubmit}
        cancelButtonText="Cerrar"
        confirmButtonText="Continuar"
      />

      <FormFooter
        saveButtonDisabled={
          loading ||
          additionalActivities.every((activity) => activity.lockedExecution)
        }
        saveButtonIcon={loading ? 'fa fa-spinner fa-spin' : 'fa fa-save'}
      />
    </Form>
  );
};

MaintenanceChecklistAdditionalExecutionForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,

  maintenanceChecklist: PropTypes.shape({
    id: PropTypes.number,
    serviceOrderId: PropTypes.number,
    registeredBusId: PropTypes.number,
    maintenanceChecklistActivityList: PropTypes.array,
  }).isRequired,

  dispatchChange: PropTypes.func.isRequired,
  dispatchSubmit: PropTypes.func.isRequired,
  dispatchReset: PropTypes.func.isRequired,

  loading: PropTypes.bool,

  maintenanceFailModeActivities: PropTypes.oneOfType([
    PropTypes.instanceOf(Immutable.List),
    PropTypes.array,
  ]),

  loadingMaintenanceFailModeActivities: PropTypes.bool,

  dispatchGetMaintenanceFailModeActivities: PropTypes.func.isRequired,

  dispatchClearMaintenanceFailModeActivities: PropTypes.func.isRequired,
};

MaintenanceChecklistAdditionalExecutionForm.defaultProps = {
  loading: false,

  maintenanceFailModeActivities: Immutable.List(),

  loadingMaintenanceFailModeActivities: false,
};

const mapStateToProps = (state) => ({
  loading: !state.MechanicalMaintenanceUnit.MaintenanceChecklist.getIn([
    'current',
    'activity',
  ]).isEmpty(),

  maintenanceFailModeActivities:
    state.MechanicalMaintenanceUnit.MaintenanceFailMode.getIn([
      'activities',
      'content',
    ]),

  loadingMaintenanceFailModeActivities:
    state.MechanicalMaintenanceUnit.MaintenanceFailMode.getIn([
      'activities',
      'loading',
    ]),
});

const mapDispatchToProps = {
  dispatchChange: change,
  dispatchSubmit: submit,
  dispatchReset: reset,

  dispatchGetMaintenanceFailModeActivities: getMaintenanceFailModeActivities,

  dispatchClearMaintenanceFailModeActivities:
    clearMaintenanceFailModeActivities,
};

const formComponent = reduxForm({
  form: MAINTENANCE_CHECKLIST_ADDITIONAL_EXECUTION_FORM_NAME,
})(MaintenanceChecklistAdditionalExecutionForm);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
